import type { LobbyServerEvent } from '@danda/shared';
import type { CharacterPermanent, CharacterSession } from '@danda/shared';
import { ActiveNPCEnum } from '@danda/shared';
import { cleanNpcResponse } from '@/lib/filters/cleanNpcResponse';
import { getSpecialNpcEmoji } from '@danda/shared/utils/avatar-emoji';
import { getStaffAvatarUrl, getVideoUrl } from '@/lib/media';
import { websocketService } from '@/lib/websocket-service';
import type { Character } from '../lobbyStore.types';
import type { EventHandlerContext } from './lobby-event-types';
import { addCompletedStreamId, addOrEnqueueMessage, msgId } from './stream-utils';

/** NPC avatar URLs for pre-made staff avatars */
const NPC_AVATARS: Record<string, string> = {
  tavian: getStaffAvatarUrl('tavian-avatar.webp'),
  aldric: getStaffAvatarUrl('aldric-avatar.webp'),
  kira: getStaffAvatarUrl('kira-avatar.webp'),
  grolt: getStaffAvatarUrl('grolt-avatar.webp'),
};

/** NPC hero video URLs. Null = no video for this NPC yet. */
const NPC_HERO_VIDEOS: Record<string, string | null> = {
  tavian: getVideoUrl('tavian.mp4'),
  aldric: getVideoUrl('aldric.mp4'),
  kira: getVideoUrl('kira.mp4'),
  grolt: getVideoUrl('grolt.mp4'),
};

/** Type guard for character data in stream end events */
function isValidCharacterData(
  data: unknown
): data is { permanent: CharacterPermanent; session?: CharacterSession } {
  if (!data || typeof data !== 'object') return false;
  const obj = data as Record<string, unknown>;
  return 'permanent' in obj && typeof obj.permanent === 'object' && obj.permanent !== null;
}

export function handleNpcEvents(event: LobbyServerEvent, ctx: EventHandlerContext): boolean {
  const { get, set } = ctx;
  const state = get();

  switch (event.type) {
    case 'lobby:tavian-response':
    case 'lobby:tavian-response-character': {
      if (!event.message || event.message.trim().length === 0) return true;
      if (event.streamId && state.completedStreamIds[event.streamId]) return true;
      const { requestSentAt: reqSentAt } = get();
      const latency = reqSentAt ? Date.now() - reqSentAt : null;
      set({
        isWaitingForResponse: false,
        requestSentAt: null,
        ttftLatency: latency,
        totalLlmLatency: latency,
      });
      if (state.mainChatNpc === ActiveNPCEnum.TAVIAN) {
        addOrEnqueueMessage(state, 'main', {
          id: msgId('tavian'),
          senderId: ActiveNPCEnum.TAVIAN,
          senderName: 'Tavian',
          content: cleanNpcResponse(event.message),
          timestamp: new Date(event.sent_at),
          type: 'tavian',
          avatar: NPC_AVATARS.tavian,
          avatarUrl: NPC_AVATARS.tavian,
          avatarEmoji: getSpecialNpcEmoji('tavian') ?? '🧙‍♂️',
        });
      }
      return true;
    }

    case 'lobby:npc-stream-start': {
      const npcType = event.npc as 'tavian' | 'aldric' | 'kira' | 'grolt' | 'inner_voice';

      // Inner Voice streams go to the inner-{charId} room, not main
      if (npcType === 'inner_voice') {
        const charId = state.selectedCharacterId;
        if (!charId) return true;
        const targetRoomId = `inner-${charId}`;
        const room = state.chatRooms.find((r) => r.id === targetRoomId);
        if (!room) return true;

        const messageId = `inner-voice-stream-${event.streamId}`;
        const { requestSentAt, activeStreams } = get();
        const ttft = requestSentAt ? Date.now() - requestSentAt : null;

        set((s) => {
          // Read room from current state (not outer snapshot) for fresh metadata
          const freshRoom = s.chatRooms.find((r) => r.id === targetRoomId);
          return {
            chatRooms: s.chatRooms.map((r) =>
              r.id === targetRoomId
                ? {
                    ...r,
                    messages: [
                      ...r.messages,
                      {
                        id: messageId,
                        senderId: 'inner_voice',
                        senderName: freshRoom?.name ?? room.name,
                        content: '',
                        timestamp: new Date(event.sent_at),
                        type: 'inner' as const,
                        isStreaming: true,
                        avatarUrl: freshRoom?.avatarUrl ?? room.avatarUrl,
                        avatarEmoji: freshRoom?.avatarEmoji ?? room.avatarEmoji,
                      },
                    ],
                  }
                : r
            ),
            activeStreams: {
              ...activeStreams,
              [event.streamId]: {
                streamId: event.streamId,
                roomId: targetRoomId,
                messageId,
                chunks: [],
              },
            },
            ttftLatency: ttft,
            streamStartedAt: requestSentAt,
            requestSentAt: null,
          };
        });
        return true;
      }

      // Only process stream if it matches the current NPC chat
      // This prevents Tavian greetings from appearing in Aldric/Kira/Grolt chats
      if (state.mainChatNpc !== npcType) {
        return true;
      }

      const NPC_NAMES: Record<string, string> = {
        tavian: 'Tavian',
        aldric: 'Aldric',
        kira: 'Kira',
        grolt: 'Grolt',
      };
      const NPC_FALLBACK_EMOJIS: Record<string, string> = {
        tavian: '🧙‍♂️',
        aldric: '🌟',
        kira: '⚔️',
        grolt: '⚒️',
      };
      const npcName = NPC_NAMES[npcType] ?? npcType;
      const npcEmoji = getSpecialNpcEmoji(npcType) ?? NPC_FALLBACK_EMOJIS[npcType] ?? '🙂';
      const npcAvatarUrl = NPC_AVATARS[npcType];
      const messageId = `${npcType}-stream-${event.streamId}`;
      const { requestSentAt, activeStreams } = get();
      const ttft = requestSentAt ? Date.now() - requestSentAt : null;

      // Lobby NPCs pretending to be human players (Kira/Aldric/Grolt) route
      // their LIVE stream through the reveal queue so `ProgressiveReveal`
      // throttles the displayed substring to ~40 WPM. Tavian + others keep
      // the existing server-paced streaming display. Mirrors the gate in
      // `apps/web/src/lib/constants/message-reveal.ts::shouldTypewriter`.
      const HUMAN_TYPING_NPCS = new Set(['kira', 'aldric', 'grolt']);
      const isHumanTypingNpc = HUMAN_TYPING_NPCS.has(npcType);

      // Append streaming message without bumping unread even if main is inactive
      set((s) => ({
        chatRooms: s.chatRooms.map((r) =>
          r.id === 'main'
            ? {
                ...r,
                messages: [
                  ...r.messages,
                  {
                    id: messageId,
                    senderId: npcType,
                    senderName: npcName,
                    content: '',
                    timestamp: new Date(event.sent_at),
                    type: npcType,
                    isStreaming: true,
                    isQueuedReveal: isHumanTypingNpc,
                    // Real WS stream — must NOT be killed by tab-switch /
                    // Escape / ArrowDown. Distinguishes this from canned
                    // reveal-queue messages that also carry `isStreaming: true`.
                    isLiveStream: isHumanTypingNpc,
                    avatar: npcAvatarUrl,
                    avatarUrl: npcAvatarUrl,
                    avatarEmoji: npcEmoji,
                  },
                ],
                unreadCount: r.unreadCount,
              }
            : r
        ),
        activeStreams: {
          ...activeStreams,
          [event.streamId]: { streamId: event.streamId, roomId: 'main', messageId, chunks: [] },
        },
        // Track this message as the currently-revealing one in 'main' so
        // `useLobbyMessageQueue` wires `onRevealComplete` through to the
        // ChatMessage props — without it, ProgressiveReveal would have no
        // `onComplete` callback and the bubble would never flip out of
        // `isQueuedReveal` mode after typing finished.
        revealingMessageIds: isHumanTypingNpc
          ? { ...s.revealingMessageIds, main: messageId }
          : s.revealingMessageIds,
        // Keep isWaitingForResponse true during streaming - only clear on stream-end
        ttftLatency: ttft,
        streamStartedAt: requestSentAt,
        requestSentAt: null,
      }));
      return true;
    }

    case 'lobby:npc-stream-chunk': {
      const { completedStreamIds, activeStreams } = get();

      // Skip if stream already completed
      if (completedStreamIds[event.streamId]) {
        return true;
      }

      // Find stream in activeStreams
      const stream = activeStreams[event.streamId];
      if (stream) {
        state.appendToMessage(stream.roomId, stream.messageId, event.chunk);
      }
      return true;
    }

    case 'lobby:npc-stream-end': {
      const { completedStreamIds, activeStreams } = get();
      const stream = activeStreams[event.streamId];

      // Mark stream as completed (pruned to last N entries)
      const newCompletedStreamIds = addCompletedStreamId(completedStreamIds, event.streamId);

      // Remove from activeStreams
      const { [event.streamId]: _removed, ...newActiveStreams } = activeStreams;

      if (stream) {
        const { streamStartedAt } = get();
        const totalLatency = streamStartedAt ? Date.now() - streamStartedAt : null;

        const cleanedMessage = cleanNpcResponse(event.fullMessage);
        const isEmptyMessage = !cleanedMessage || cleanedMessage.trim().length === 0;

        if (isEmptyMessage) {
          set((s) => ({
            chatRooms: s.chatRooms.map((r) =>
              r.id === stream.roomId
                ? {
                    ...r,
                    messages: r.messages.filter((m) => m.id !== stream.messageId),
                    unreadCount: r.unreadCount,
                  }
                : r
            ),
            activeStreams: newActiveStreams,
            completedStreamIds: newCompletedStreamIds,
            totalLlmLatency: totalLatency,
            streamStartedAt: null,
            // Clear the reveal-id pointer that NpcStreamStart registered: the
            // bubble was deleted (filter above), so ProgressiveReveal's
            // onComplete never fires. Without this, `revealingMessageIds` is
            // stuck non-null and `advanceRevealQueue` stalls until the
            // 5.5s watchdog in useLobbyMessageQueue force-finishes it.
            revealingMessageIds: {
              ...s.revealingMessageIds,
              [stream.roomId]:
                s.revealingMessageIds[stream.roomId] === stream.messageId
                  ? null
                  : s.revealingMessageIds[stream.roomId],
            },
          }));
        } else {
          set((s) => ({
            chatRooms: s.chatRooms.map((r) =>
              r.id === stream.roomId
                ? {
                    ...r,
                    messages: r.messages.map((m) =>
                      m.id === stream.messageId
                        ? {
                            ...m,
                            content: cleanedMessage,
                            isStreaming: false,
                            // The WS wire is done; the typewriter catchup is
                            // just animation now. Clear `isLiveStream` so the
                            // user can ArrowDown / Escape / tab-switch
                            // freely — those gates only need to protect the
                            // mid-stream window. Avoids the stuck-flag case
                            // when Virtuoso virtualizes the bubble before
                            // any reveal char rendered (cleanup-onComplete
                            // guard skips firing when posRef=0).
                            isLiveStream: false,
                          }
                        : m
                    ),
                    unreadCount: r.unreadCount,
                  }
                : r
            ),
            activeStreams: newActiveStreams,
            completedStreamIds: newCompletedStreamIds,
            totalLlmLatency: totalLatency,
            streamStartedAt: null,
          }));
        }

        // Backend sends characterData (object) or characterFile (JSON string of permanent)
        const eventWithData = event as typeof event & {
          characterData?: unknown;
          characterFile?: string;
        };
        let characterData = eventWithData.characterData;
        // Fallback: parse characterFile (JSON string) into characterData format
        if (!characterData && eventWithData.characterFile) {
          try {
            const permanent = JSON.parse(eventWithData.characterFile);
            characterData = { permanent };
          } catch {
            console.warn('[NPC Events] Failed to parse characterFile JSON');
          }
        }

        if (event.charId) {
          console.log('[NPC Events] Stream end char data', {
            charId: event.charId,
            isComplete: event.isComplete,
            hasCharacterData: !!characterData,
            hasCharacterFile: !!eventWithData.characterFile,
            isValid: isValidCharacterData(characterData),
          });
          state.setCharCreationAvailable(false);
          if (event.isComplete && isValidCharacterData(characterData)) {
            // Character creation complete - add to store and select
            const permanent = characterData.permanent;
            const session = characterData.session;
            const charName =
              typeof permanent.name === 'string' && permanent.name.trim().length > 0
                ? permanent.name
                : 'Unknown';
            const avatarUrl =
              (
                (permanent.avatar_url as string | undefined) ||
                (permanent.avatarUrl as string | undefined) ||
                ''
              )?.trim() || undefined;
            const newCharacter: Character = {
              id: event.charId,
              name: charName,
              permanent: permanent as CharacterPermanent,
              session: session as CharacterSession,
              avatar: avatarUrl || charName,
              avatarUrl,
              avatarEmoji: permanent.avatar_emoji,
            };
            state.addCharacter(newCharacter);
            state.selectCharacter(event.charId);
            state.setCharCreationCharId(null); // Clear creation charId
            // One-time Begin Your Adventure hero overlay for the fresh PC.
            // Reads character state (not chat state), so it survives the
            // RedirectToNpc that follows this stream-end.
            state.setHeroOverlayCharId(event.charId);
            // Drives the one-time WelcomePanel orientation card once the
            // player reaches the post-character lobby (see that flag's doc).
            state.setJustCreatedCharacter(true);

            // Provision the inner-voice (1:1 self-chat) tab for the new PC and
            // fire its [WELCOME] trigger so the room is populated when the
            // user later opens it (e.g. by clicking their PC card in the
            // ScenePanel). We deliberately do NOT setActiveChatRoomId here:
            // the BE follows stream-end with a RedirectToNpc → switchMainNpc,
            // which forces activeChatRoomId='main', so any switch we make
            // would be overwritten anyway. Mirrors the inner-voice setup in
            // session-events.ts:syncCharacterData (used on session load).
            const innerRoomId = `inner-${event.charId}`;
            // Remove stale inner rooms for any other character (defensive — a
            // fresh PC shouldn't coexist with another PC's inner-voice room)
            const staleInnerRooms = state.chatRooms.filter(
              (r) => r.type === 'inner' && r.id !== innerRoomId
            );
            for (const room of staleInnerRooms) {
              state.removeChatRoom(room.id);
            }
            const hasInnerRoom = state.chatRooms.some((r) => r.id === innerRoomId);
            if (!hasInnerRoom) {
              state.addChatRoom({
                id: innerRoomId,
                name: charName,
                type: 'inner',
                participants: [event.charId],
                messages: [],
                unreadCount: 0,
                avatarUrl,
                avatarEmoji: permanent.avatar_emoji,
              });
              // Fire-and-forget welcome — same pattern as session-events.ts.
              setTimeout(async () => {
                try {
                  const { default: ws } = await import('../../websocket-service');
                  ws.sendChatMessage(innerRoomId, '[WELCOME]');
                } catch (e) {
                  console.warn('[LobbyStore] Failed to send inner voice welcome:', e);
                }
              }, 500);
            }
          } else {
            // Character creation in progress - store charId for subsequent messages
            // Do NOT call selectCharacter here as it triggers the loading guard
            state.setCharCreationCharId(event.charId);
          }
        }
        // Inner voice streams always clear waiting; main chat NPCs only clear if
        // the stream matches the current NPC (avoids race when Tavian redirects to Aldric)
        if (stream.messageId.startsWith('inner-voice-stream-')) {
          state.setWaitingForResponse(false);
        } else {
          const streamNpcMatch = stream.messageId.match(/^(tavian|aldric|kira|grolt)-stream-/);
          const streamNpcType = streamNpcMatch?.[1] as
            | 'tavian'
            | 'aldric'
            | 'kira'
            | 'grolt'
            | undefined;
          if (streamNpcType && streamNpcType === state.mainChatNpc) {
            state.setWaitingForResponse(false);
          }
        }
      }
      return true;
    }

    case 'lobby:char-create-response': {
      // Dedupe path: no side effects, just skip
      if (event.streamId && state.completedStreamIds[event.streamId]) {
        return true;
      }
      const { requestSentAt: aldricReqSentAt } = get();
      const aldricLatency = aldricReqSentAt ? Date.now() - aldricReqSentAt : null;
      // Check if this is the first Aldric message (before switching NPC)
      const isFirstAldricMessage = state.mainChatNpc !== ActiveNPCEnum.ALDRIC;
      set({
        isWaitingForResponse: false,
        requestSentAt: null,
        ttftLatency: aldricLatency,
        totalLlmLatency: aldricLatency,
      });
      state.startAldricChat();
      // Don't select the character until we have fetched data; selecting without data
      // causes the loader guard (selectedCharacterId && !hasCharacter) to stick.
      if (event.charId) {
        state.setCharCreationCharId(event.charId);
      }
      const cleanedResponse = cleanNpcResponse(event.response);
      // Add hero video to first Aldric message (character creation intro)
      const content = isFirstAldricMessage
        ? `[NPC_HERO:${NPC_HERO_VIDEOS.aldric}]\n\n${cleanedResponse}`
        : cleanedResponse;
      addOrEnqueueMessage(state, 'main', {
        id: msgId('aldric'),
        senderId: 'aldric',
        senderName: 'Brother Aldric',
        content,
        timestamp: new Date(event.sent_at),
        type: 'aldric',
        avatar: NPC_AVATARS.aldric,
        avatarUrl: NPC_AVATARS.aldric,
        avatarEmoji: getSpecialNpcEmoji('aldric') ?? '🌟',
      });
      state.setCharCreationAvailable(false);

      if (event.isComplete && event.charId) {
        // Keep charCreationCharId so lobby:session-state can hydrate.
        // Don't clear selection or switch NPC here; wait for lobby:session-state.
        state.setCharCreationCharId(event.charId);
        state.setLoading(false);
        state.setWaitingForResponse(false);
      }
      return true;
    }

    case 'lobby:char-create-available':
      state.setCharCreationAvailable(true);
      return true;

    case 'lobby:level-up-response': {
      // Level-up complete signal from backend.
      // The Kira message was already streamed via npc-stream-start/chunk/end —
      // this event only triggers the NPC switch back to Tavian.
      set({
        isWaitingForResponse: false,
        requestSentAt: null,
      });
      if (event.isComplete) {
        state.setLoading(true);
        state.returnToTavian();
      }
      return true;
    }

    case 'lobby:level-up-available':
      // This event fires when level-up is available - always show the message
      state.addMessage('main', {
        id: msgId('system'),
        senderId: 'system',
        senderName: 'System',
        content: '⭐ You have enough experience to level up! Speak with Master Kira to advance.',
        timestamp: new Date(),
        type: 'system',
      });
      return true;

    case 'lobby:redirect-to-npc': {
      console.log('[NPC Events] Redirect to NPC', {
        targetNpc: event.targetNpc,
        reason: event.reason,
        currentNpc: state.mainChatNpc,
      });
      // Finalize any active streams in the main room before redirecting
      const { activeStreams } = get();
      const mainStreams = Object.values(activeStreams).filter((s) => s.roomId === 'main');
      if (mainStreams.length > 0) {
        // Remove main room streams from activeStreams
        const newActiveStreams = Object.fromEntries(
          Object.entries(activeStreams).filter(([_, s]) => s.roomId !== 'main')
        );

        set((s) => ({
          chatRooms: s.chatRooms.map((r) =>
            r.id === 'main'
              ? {
                  ...r,
                  messages: r.messages.map((m) =>
                    mainStreams.some((stream) => stream.messageId === m.id)
                      ? { ...m, isStreaming: false }
                      : m
                  ),
                }
              : r
          ),
          activeStreams: newActiveStreams,
        }));
      }

      // If leaving Grolt, close the merchant session
      if (state.mainChatNpc === ActiveNPCEnum.GROLT && event.targetNpc !== ActiveNPCEnum.GROLT) {
        if (state.selectedCharacterId) {
          websocketService.closeMerchantSession(state.selectedCharacterId);
        } else {
          console.warn(
            '[npc-events] Leaving Grolt with no selectedCharacterId — merchant session may not have been opened'
          );
        }
      }

      state.switchMainNpc(event.targetNpc);
      if (event.targetNpc === ActiveNPCEnum.ALDRIC) {
        state.setWaitingForResponse(true);
        websocketService.startCharacterCreationSession();
      } else if (event.targetNpc === ActiveNPCEnum.KIRA) {
        state.setWaitingForResponse(true);
        // Start level-up session when redirecting to Kira
        if (state.selectedCharacterId) {
          websocketService.startLevelUp(state.selectedCharacterId);
        }
      } else if (event.targetNpc === ActiveNPCEnum.GROLT) {
        state.setWaitingForResponse(true);
        if (state.selectedCharacterId) {
          websocketService.startMerchantSession(state.selectedCharacterId);
        }
      } else {
        state.setWaitingForResponse(false);
      }
      return true;
    }

    // Tavian tool execution result (from Rust backend)
    case 'lobby:tavian-tool-result': {
      // Tool results are typically not displayed directly to users
      // but could be logged or used for debugging
      console.debug('[Tavian Tool Result]', event.toolName, event.result);
      return true;
    }

    // Level-up cancelled (from Rust backend)
    case 'lobby:levelup-cancelled': {
      // Return to Tavian when level-up is cancelled
      state.returnToTavian();
      state.setWaitingForResponse(false);
      state.addMessage('main', {
        id: msgId('system'),
        senderId: 'system',
        senderName: 'System',
        content: 'Level-up session was cancelled.',
        timestamp: new Date(event.sent_at),
        type: 'system',
      });
      return true;
    }

    default:
      return false;
  }
}
